import React, { useState, useEffect, ChangeEvent, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Book } from '@/types'
import { supabase } from '../../lib/supabase'
import { FIELD_LIMITS, truncateField } from '../../utils/validation'
import CustomConfirmModal from '../common/CustomConfirmModal'
import BookDetailModal from '../BookDetailModal'
import styles from './ListsPage.module.css'

const formatAuthors = (authors: string[] | string | null | undefined): string => {
    if (!authors) return 'Desconocido'
    if (Array.isArray(authors)) return authors.join(', ') || 'Desconocido'
    return authors || 'Desconocido'
}

const formatCategories = (categories: string[] | string | null | undefined): string => {
    if (!categories) return '-'
    if (Array.isArray(categories)) return categories.join(', ') || '-'
    return categories || '-'
}

const DEFAULT_VISIBLE_COLUMNS = {
    series: true,
    series_number: false,
    category: true,
    language: true,
    progress: true,
    format: false,
    status: false,
    location: false,
}

type VisibleColumns = typeof DEFAULT_VISIBLE_COLUMNS

interface ReadingListItem {
    id: string
    name: string
    created_at: string
    reading_list_items: Array<{ count: number }>
}

interface ReadingList {
    id: string
    name: string
    created_at: string
    item_count: number
    items: Book[]
}

interface NotificationState {
    isOpen: boolean
    title: string
    message: string
    type: 'success' | 'danger' | 'warning' | 'info'
}

export default function ListsPage() {
    const { t } = useTranslation()
    const [lists, setLists] = useState<ReadingList[]>([])
    const [loading, setLoading] = useState(true)
    const [newListName, setNewListName] = useState('')
    const [creating, setCreating] = useState(false)
    const [expandedListId, setExpandedListId] = useState<string | null>(null)
    const [listToDelete, setListToDelete] = useState<string | null>(null)
    const [selectedBook, setSelectedBook] = useState<Book | null>(null)
    const [showColumnSettings, setShowColumnSettings] = useState(false)
    const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>(() => {
        const saved = localStorage.getItem('bibliotelia_lists_columns')
        if (!saved) return DEFAULT_VISIBLE_COLUMNS
        try {
            const parsed = JSON.parse(saved)
            const validated: Partial<VisibleColumns> = {}
            Object.keys(DEFAULT_VISIBLE_COLUMNS).forEach((key) => {
                if (parsed && typeof parsed[key] === 'boolean') {
                    validated[key as keyof VisibleColumns] = parsed[key]
                } else {
                    validated[key as keyof VisibleColumns] = DEFAULT_VISIBLE_COLUMNS[key as keyof VisibleColumns]
                }
            })
            return validated as VisibleColumns
        } catch (error) {
            console.error('Error parsing visible columns from localStorage:', error)
            return DEFAULT_VISIBLE_COLUMNS
        }
    })

    const [notification, setNotification] = useState<NotificationState>({ isOpen: false, title: '', message: '', type: 'info' })
    const showNotify = (title: string, message: string, type: NotificationState['type'] = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }

    const toggleColumn = (column: string) => {
        setVisibleColumns((prev) => {
            if (!Object.hasOwn(DEFAULT_VISIBLE_COLUMNS, column)) {
                return prev
            }
            const next = { ...prev, [column]: !prev[column as keyof VisibleColumns] }
            localStorage.setItem('bibliotelia_lists_columns', JSON.stringify(next))
            return next
        })
    }

    const fetchLists = async () => {
        try {
            const { data: user } = await supabase.auth.getUser()
            if (!user.user) return

            const { data: listsData, error: listsError } = await supabase
                .from('reading_lists')
                .select(
                    `
          id, 
          name, 
          created_at,
          reading_list_items (
            count
          )
        `
                )
                .eq('user_id', user.user.id)
                .order('created_at', { ascending: false })

            if (listsError) throw listsError

            const formattedLists = (listsData as ReadingListItem[]).map((list) => ({
                ...list,
                item_count: list.reading_list_items[0]?.count || 0,
                items: [],
            }))

            setLists(formattedLists)
        } catch (error) {
            console.error('Error fetching lists:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchLists()
    }, [])

    // eslint-disable-next-line sonarjs/deprecation
    const handleCreateList = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!newListName.trim()) return

        setCreating(true)
        try {
            const { data: user } = await supabase.auth.getUser()

            const { data, error } = await supabase
                .from('reading_lists')
                .insert([
                    {
                        name: truncateField(newListName.trim(), FIELD_LIMITS.readingLists.name),
                        user_id: user.user?.id,
                    },
                ])
                .select()

            if (error) throw error

            setLists([{ ...(data[0] as unknown as ReadingList), item_count: 0, items: [] }, ...lists])
            setNewListName('')
        } catch (error) {
            console.error('Error creating list:', error)
            showNotify('Error', t('listsPage.errors.create'), 'danger')
        } finally {
            setCreating(false)
        }
    }

    const handleDeleteList = async (e: MouseEvent, listId: string) => {
        e.stopPropagation()
        setListToDelete(listId)
    }

    const handleBookClick = (book: Book) => {
        setSelectedBook(book)
    }

    const confirmDeleteList = async () => {
        if (!listToDelete) return
        const listId = listToDelete
        setListToDelete(null)

        try {
            const { error } = await supabase.from('reading_lists').delete().eq('id', listId)

            if (error) throw error

            setLists(lists.filter((l) => l.id !== listId))
            if (expandedListId === listId) setExpandedListId(null)
        } catch (error) {
            console.error('Error deleting list:', error)
            showNotify('Error', t('listsPage.errors.deleteList'), 'danger')
        }
    }

    const toggleExpand = async (listId: string) => {
        if (expandedListId === listId) {
            setExpandedListId(null)
            return
        }

        setExpandedListId(listId)

        const list = lists.find((l) => l.id === listId)
        if (!list) return

        if (!list.items || list.items.length === 0) {
            await fetchListItems(listId)
        }
    }

    const fetchListItems = async (listId: string) => {
        try {
            const { data, error } = await supabase
                .from('reading_list_items')
                .select(
                    `
          book_id,
          added_at,
          books (
            id,
            title,
            author,
            language,
            category,
            series,
            series_number,
            progress,
            format,
            status,
            location
          )
        `
                )
                .eq('list_id', listId)
                .order('added_at', { ascending: false })

            if (error) throw error

            const items = (data || [])
                .filter((item) => item.books !== null && (Array.isArray(item.books) ? item.books.length > 0 : true))
                .map((item) => {
                    // Safe cast as we know the structure from the query
                    const bookData = Array.isArray(item.books) ? item.books[0] : item.books
                    return {
                        ...bookData,
                        added_at: item.added_at,
                    } as unknown as Book
                })

            setLists((prev) => prev.map((list) => (list.id === listId ? { ...list, items: items } : list)))
        } catch (error) {
            console.error('Error fetching list items:', error)
        }
    }

    const handleRemoveItem = async (listId: string, bookId: string) => {
        try {
            const { error } = await supabase.from('reading_list_items').delete().match({ list_id: listId, book_id: bookId })

            if (error) throw error

            const updateList = (list: ReadingList) => {
                if (list.id !== listId) return list
                return {
                    ...list,
                    item_count: Math.max(0, list.item_count - 1),
                    items: list.items ? list.items.filter((item) => item.id !== bookId) : [],
                }
            }
            setLists((prev) => prev.map(updateList))
        } catch (error) {
            console.error('Error removing item:', error)
            showNotify('Error', t('listsPage.errors.deleteItem'), 'danger')
        }
    }

    if (loading) {
        return (
            <div className={styles.listsContainer}>
                <div className={styles.loadingState}>
                    <div className={`${styles.spinner} ${styles.large}`}></div>
                    <p>{t('listsPage.loading')}</p>
                </div>
            </div>
        )
    }

    return (
        <div className={`${styles.listsContainer} animate-fade-in-up`}>
            <div className={styles.listsHeader}>
                <h2 className={styles.listsTitle}>
                    <span className={styles.titleIcon}>📑</span> {t('listsPage.title')}
                </h2>

                <div className={styles.listsActions}>
                    <div className={styles.settingsWrapper}>
                        <button
                            className={`${styles.settingsIconBtn} ${styles.pageHeaderBtn}`}
                            onClick={(e) => {
                                e.stopPropagation()
                                setShowColumnSettings(!showColumnSettings)
                            }}
                            title={t('listsPage.columns.configure')}
                        >
                            ⚙️
                        </button>
                        {showColumnSettings && (
                            <div className={styles.columnSettingsMenu} role="menu" aria-label={t('listsPage.columns.ariaLabel')}>
                                <div className={styles.menuHeader}>{t('listsPage.columns.visible')}</div>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.series} onChange={() => toggleColumn('series')} />
                                    <span>{t('listsPage.columns.series')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.series_number} onChange={() => toggleColumn('series_number')} />
                                    <span>{t('listsPage.columns.seriesNumber')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.category} onChange={() => toggleColumn('category')} />
                                    <span>{t('listsPage.columns.category')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.language} onChange={() => toggleColumn('language')} />
                                    <span>{t('listsPage.columns.language')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.progress} onChange={() => toggleColumn('progress')} />
                                    <span>{t('listsPage.columns.progress')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.format} onChange={() => toggleColumn('format')} />
                                    <span>{t('listsPage.columns.format')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.status} onChange={() => toggleColumn('status')} />
                                    <span>{t('listsPage.columns.status')}</span>
                                </label>
                                <label className={styles.menuOption}>
                                    <input type="checkbox" checked={visibleColumns.location} onChange={() => toggleColumn('location')} />
                                    <span>{t('listsPage.columns.location')}</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <form className={styles.createListSection} onSubmit={handleCreateList} data-testid="create-list-form">
                        <div className={styles.newListInputWrapper}>
                            <input
                                type="text"
                                className={styles.newListInput}
                                placeholder={t('listsPage.create.placeholder')}
                                value={newListName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewListName(truncateField(e.target.value, FIELD_LIMITS.readingLists.name))}
                                disabled={creating}
                                maxLength={FIELD_LIMITS.readingLists.name}
                                data-testid="new-list-input"
                            />
                            <span className={`${styles.charCounter} ${newListName.length >= FIELD_LIMITS.readingLists.name * 0.9 ? styles.warning : ''}`}>
                                {newListName.length}/{FIELD_LIMITS.readingLists.name}
                            </span>
                        </div>
                        <button type="submit" className={styles.createListBtn} disabled={creating || !newListName.trim()} data-testid="create-list-btn">
                            {creating ? <span className={`${styles.spinner} small`}></span> : t('listsPage.create.button')}
                        </button>
                    </form>
                </div>
            </div>

            <div className={styles.readingListsWrapper}>
                {lists.length === 0 ? (
                    <div className={styles.emptyState}>
                        <span className={styles.emptyIcon}>📝</span>
                        <p className={styles.emptyText}>{t('listsPage.empty.title')}</p>
                        <p className={styles.emptySubtext}>{t('listsPage.empty.subtitle')}</p>
                    </div>
                ) : (
                    lists.map((list) => (
                        <div key={list.id} className={styles.listCard} data-testid={`list-card-${list.name}`}>
                            <div className={styles.listHeaderWrapper}>
                                <button
                                    className={styles.listHeader}
                                    onClick={() => toggleExpand(list.id)}
                                    aria-expanded={expandedListId === list.id}
                                    type="button"
                                    data-testid={`list-header-${list.name}`}
                                >
                                    <div className={styles.listInfo}>
                                        <span className={styles.listIcon}>📂</span>
                                        <div className={styles.listNameWrapper}>
                                            <span className={styles.listName} data-testid={`list-name-${list.name}`}>{list.name}</span>
                                            <span className={styles.listCount}>
                                                {t('listsPage.item.count', { count: list.item_count })}
                                            </span>
                                        </div>
                                    </div>

                                    <span className={`${styles.listExpandIcon} ${expandedListId === list.id ? styles.open : ''}`}>▼</span>
                                </button>

                                <button
                                    className={styles.deleteListBtn}
                                    onClick={(e) => handleDeleteList(e, list.id)}
                                    title={t('listsPage.item.delete')}
                                    type="button"
                                    data-testid={`delete-list-btn-${list.name}`}
                                >
                                    🗑️
                                </button>
                            </div>

                            {expandedListId === list.id && (
                                <div className={styles.listContent} data-testid={`list-content-${list.name}`}>
                                    {list.items && list.items.length > 0 ? (
                                        <table className={styles.listBooksTable}>
                                            <thead>
                                                <tr>
                                                    <th>{t('listsPage.columns.title')}</th>
                                                    <th>{t('listsPage.columns.author')}</th>
                                                    {visibleColumns.series && <th>{t('listsPage.columns.series')}</th>}
                                                    {visibleColumns.series_number && <th>#</th>}
                                                    {visibleColumns.category && <th>{t('listsPage.columns.category')}</th>}
                                                    {visibleColumns.language && <th>{t('listsPage.columns.language')}</th>}
                                                    {visibleColumns.progress && <th>{t('listsPage.columns.progress')}</th>}
                                                    {visibleColumns.format && <th>{t('listsPage.columns.format')}</th>}
                                                    {visibleColumns.status && <th>{t('listsPage.columns.status')}</th>}
                                                    {visibleColumns.location && <th>{t('listsPage.columns.location')}</th>}
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {list.items.map((book) => (
                                                    <tr key={book.id} className={`${styles.listBookRow} ${styles.clickable}`}>
                                                        <td>
                                                            <button
                                                                className={styles.tableRowActionBtn}
                                                                onClick={() => handleBookClick(book)}
                                                                aria-label={t('listsPage.item.viewDetails', { title: book.title })}
                                                                type="button"
                                                            >
                                                                {book.title}
                                                            </button>
                                                        </td>
                                                        <td>{formatAuthors(book.author)}</td>
                                                        {visibleColumns.series && <td>{book.series || '-'}</td>}
                                                        {visibleColumns.series_number && <td>{book.series_number || '-'}</td>}
                                                        {visibleColumns.category && <td>{formatCategories(book.category)}</td>}
                                                        {visibleColumns.language && <td>{book.language || '-'}</td>}
                                                        {visibleColumns.progress && (
                                                            <td>{book.progress && <span className={`${styles.bookStatusBadge} ${styles.small}`}>{book.progress}</span>}</td>
                                                        )}
                                                        {visibleColumns.format && <td>{book.format || '-'}</td>}
                                                        {visibleColumns.status && <td>{book.status || '-'}</td>}
                                                        {visibleColumns.location && <td>{book.location || '-'}</td>}
                                                        <td style={{ textAlign: 'right' }}>
                                                            <button
                                                                className={styles.removeBookBtn}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleRemoveItem(list.id, book.id)
                                                                }}
                                                                title={t('listsPage.item.removeBook')}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className={styles.emptyListMessage}>{t('listsPage.empty.listEmpty')}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            <CustomConfirmModal
                isOpen={!!listToDelete}
                type="danger"
                title={t('listsPage.modals.deleteTitle')}
                message={t('listsPage.modals.deleteMessage')}
                confirmText={t('listsPage.modals.deleteConfirm')}
                onConfirm={confirmDeleteList}
                onCancel={() => setListToDelete(null)}
            />

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText={t('bookList.modals.deleteBook.understood', { defaultValue: 'Entendido' })}
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
            />
            {selectedBook && <BookDetailModal book={selectedBook} onClose={() => setSelectedBook(null)} readOnly={true} ownerName={t('listsPage.title')} />}
        </div>
    )
}
